/**
 * App Catalogue — Recommendations page.
 *
 * Renders the signed-in admin's ranked app recommendations and lets an admin
 * trigger an on-demand recompute for every user.
 *
 * The SDK's `ApplicationCatalogueService` does not (yet) wrap the
 * recommendations endpoints, so — exactly like the Infrastructure Monitoring
 * hooks — we call them via the raw `useHttpClient()` against the gateway
 * prefix:
 *   GET  /application-catalogue/api/catalogue/recommendations
 *        -> ResponseData<AppRecommendationView[]>
 *   POST /application-catalogue/api/catalogue/recommendations/recompute (admin)
 *        -> ResponseData<{ usersProcessed: number }>
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconSparkles,
  IconRefresh,
  IconExternalLink,
  IconArrowLeft,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Title,
  Text,
  Badge,
  Paper,
  ThemeIcon,
  Button,
  Alert,
  Loader,
  Center,
  Avatar,
  toast,
} from '@/components/ui';
import { ActionIcon as MantineActionIcon } from '@mantine/core';
import { FadeIn, HoverLift } from '@/components/animations';
import { useHttpClient } from '@actschurch/ccms-sdk';
import type { AppManifest } from '@actschurch/ccms-sdk';
import { getApiUrl } from '@/config/ccms';
import { ADMIN_ROUTES } from '@/config/routes';

/**
 * Mirrors the backend `AppRecommendationView` (manifest-enriched + score).
 * The SDK has no model for it yet, so it is declared locally.
 */
interface AppRecommendationView {
  appId: string;
  score: number;
  reason: string;
  computedAt: string;
  manifest: AppManifest;
}

const RECOMMENDATIONS_QUERY_KEY = ['admin', 'app-catalogue', 'recommendations'];
const RECOMMENDATIONS_PATH = getApiUrl('applicationCatalogue', '/api/catalogue/recommendations');
const RECOMPUTE_PATH = getApiUrl(
  'applicationCatalogue',
  '/api/catalogue/recommendations/recompute'
);

export default function RecommendationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const httpClient = useHttpClient();
  const [recomputing, setRecomputing] = useState(false);

  const {
    data: recommendations = [],
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: RECOMMENDATIONS_QUERY_KEY,
    queryFn: async () => {
      const response = await httpClient.get<AppRecommendationView[]>(RECOMMENDATIONS_PATH);
      return response.data ?? [];
    },
  });

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const response = await httpClient.post<{ usersProcessed: number }>(RECOMPUTE_PATH);
      const processed = response.data?.usersProcessed ?? 0;
      toast.success(`Recommendations recomputed for ${processed} user(s)`);
      await queryClient.invalidateQueries({ queryKey: RECOMMENDATIONS_QUERY_KEY });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to recompute recommendations');
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <FadeIn>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconArrowLeft size={14} />}
              onClick={() => navigate(ADMIN_ROUTES.APP_CATALOGUE_OVERVIEW)}
            >
              Back to overview
            </Button>
            <Title order={2} mt="xs">
              Recommendations
            </Title>
            <Text c="dimmed" size="sm">
              Ranked apps the recommendation engine suggests for the signed-in admin. Recompute runs
              the engine for every user.
            </Text>
          </div>
          <Group>
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={() => queryClient.invalidateQueries({ queryKey: RECOMMENDATIONS_QUERY_KEY })}
              loading={isFetching}
              size="sm"
            >
              Refresh
            </Button>
            <Button
              leftSection={<IconSparkles size={16} />}
              onClick={handleRecompute}
              loading={recomputing}
            >
              Recompute
            </Button>
          </Group>
        </Group>

        {error ? (
          <Alert color="red" title="Service Unavailable">
            <Text size="sm">
              {error instanceof Error ? error.message : 'Failed to load recommendations.'}
            </Text>
            <Button
              variant="light"
              color="red"
              size="xs"
              mt="sm"
              onClick={() => queryClient.invalidateQueries({ queryKey: RECOMMENDATIONS_QUERY_KEY })}
            >
              Retry
            </Button>
          </Alert>
        ) : isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : recommendations.length === 0 ? (
          <Paper withBorder p="xl" ta="center">
            <Text c="dimmed">
              No recommendations yet. Recommendations are precomputed daily — use Recompute to
              generate them now.
            </Text>
          </Paper>
        ) : (
          <Stack gap="sm">
            {recommendations.map((rec, index) => (
              <HoverLift key={rec.appId} lift={2}>
                <Card withBorder padding="md" radius="md">
                  <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <ThemeIcon size="lg" radius="xl" variant="light" color="violet">
                        <Text fw={700}>{index + 1}</Text>
                      </ThemeIcon>
                      <Avatar
                        src={rec.manifest?.iconUrl || undefined}
                        radius="md"
                        size="md"
                        style={{ backgroundColor: rec.manifest?.colors?.primary || undefined }}
                      >
                        {rec.manifest?.shortName?.charAt(0) ?? rec.appId.charAt(0)}
                      </Avatar>
                      <div>
                        <Text fw={600} lineClamp={1}>
                          {rec.manifest?.displayName || rec.appId}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {rec.appId}
                        </Text>
                        {rec.reason && (
                          <Text size="sm" c="dimmed" mt={4}>
                            {rec.reason}
                          </Text>
                        )}
                      </div>
                    </Group>
                    <Group gap="sm" wrap="nowrap" align="center">
                      <Badge size="lg" variant="light" color="violet">
                        Score {rec.score}
                      </Badge>
                      {rec.manifest?.webUrl && (
                        <MantineActionIcon
                          variant="subtle"
                          size="sm"
                          color="gray"
                          component="a"
                          href={rec.manifest.webUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <IconExternalLink size={14} />
                        </MantineActionIcon>
                      )}
                    </Group>
                  </Group>
                </Card>
              </HoverLift>
            ))}
          </Stack>
        )}
      </Stack>
    </FadeIn>
  );
}
